// src/pages/AddApi.tsx

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import PageTitle from "../components/common/PageTitle";
import { apiService } from "../api/services/apiService";
import { EntityStatus } from "../types";
import { resolveReturnDestination } from "../utils/navigationReturn";

interface AddApiProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const AddApi: React.FC<AddApiProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const goBack = () =>
    navigate(resolveReturnDestination(returnPath, returnSearch, "apis"));

  /* -------------------- Form state -------------------- */
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [version, setVersion] = useState("");
  const [status, setStatus] = useState<EntityStatus>(EntityStatus.DRAFT);

  /* -------------------- File state -------------------- */
  const [file, setFile] = useState<File | null>(null);
  const [specContent, setSpecContent] = useState<string>("");

  /* -------------------- UI state -------------------- */
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /* -------------------- File handler -------------------- */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== "application/json") {
      setError("Only JSON files are allowed");
      setFile(null);
      return;
    }

    try {
      const text = await selectedFile.text();
      JSON.parse(text); // validate JSON
      setFile(selectedFile);
      setSpecContent(text);
      setError(null);
    } catch {
      setError("Invalid JSON file");
      setFile(null);
    }
  };

  /* -------------------- Submit -------------------- */
  const handleCreateApi = async () => {
    if (!file) return;

    setSubmitting(true);
    setError(null);

    try {
      await apiService.createApi({
        tenantId: tenantId || '',
        name,
        description,
        status,
        version,
        specContent,
      });

      goBack();
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to create API");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageTitle
        pageTitle="Add API Spec"
        pageDescription="Create a new API by uploading a Swagger/OpenAPI JSON specification."
      />

      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            maxWidth: "70%",
            border: "1px solid #e0e0e0",
          }}
        >
          <Stack spacing={3}>
            {/* Name */}
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />

            {/* Description */}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />

            {/* Version */}
            <TextField
              label="Version"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              fullWidth
              required
            />

            <TextField
              select
              label="Tenant ID"
              value={tenantId}
              //onChange={(e) => setTenantId(e.target.value)}
              fullWidth
            >
              <MenuItem value={tenantId || ''}>
                {tenantId}
              </MenuItem>
            </TextField>

            {/* Status */}
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as EntityStatus)}
              fullWidth
            >
              {Object.values(EntityStatus).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>

            {/* Upload */}
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                Upload Spec Document
              </Typography>

              <input
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
              />

              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected file: <strong>{file.name}</strong>
                </Typography>
              )}
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <Button
                variant="contained"
                onClick={handleCreateApi}
                disabled={submitting || !file || !name || !version}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {submitting ? <CircularProgress size={18} /> : "Add API"}
              </Button>

              <Button
                variant="outlined"
                onClick={goBack}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default AddApi;
